import React from "react";
import Container from "@/components/marketing/Container";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/marketing/Reveal";

const CHANNELS = [
  {
    name: "Journalist Outreach",
    badge: "Gold Standard",
    description: "Real journalists choosing to cover your story. We handle the pitching; the coverage is earned.",
    price: "$150",
    per: "Per Release",
    features: [
      "Targeted pitching from our 100,000+ journalist database",
      "Managed end to end by our in-house PR team",
      "Full report with every published link",
    ],
    exploreLabel: "Explore Journalist Outreach",
    exploreTo: "/services/journalist-outreach",
    featured: true,
  },
  {
    name: "Newswire Distribution",
    badge: null,
    description: "Syndicate your release across recognized wire networks for broad, fast reach.",
    price: "from $300",
    per: "Per Release",
    features: [
      "20+ newswires, including PR Newswire and Globe Newswire",
      "Regional coverage across Asia, the Middle East, Europe, and North America",
      "Sector-specific wires, from finance to crypto",
    ],
    exploreLabel: "Explore Newswire Distribution",
    exploreTo: "/services/newswire-distribution",
    featured: false,
  },
  {
    name: "Guaranteed Placements",
    badge: null,
    description: "A named, permanent article on a specific publication you choose.",
    price: "from $100",
    per: "Per Outlet",
    features: [
      "320+ publications, from niche titles to household names",
      "Across business, lifestyle, fashion, automotive, and more",
      "Indexed on Google, working long after publish",
    ],
    exploreLabel: "Explore Guaranteed Placements",
    exploreTo: "/services/guaranteed-placements",
    featured: false,
  },
];

export default function DistributionExplainer() {
  return (
    <section className="py-11 lg:py-12 border-t border-[rgba(28,28,32,0.06)]">
      <Container>
        {/* Heading */}
        <Reveal>
          <div className="text-center mb-10">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#6d28d9] mb-3.5">The Channels</div>
            <h2 className="font-heading text-[clamp(32px,4vw,46px)] font-[650] tracking-[-0.03em] leading-[1.06] mb-3.5 text-heading-gradient">
              How our distribution works.
            </h2>
            <p className="text-[16px] leading-[1.55] text-[#6c6c74] max-w-[480px] mx-auto">
              Three channels, one platform. Run any one on its own, or combine them in a single order. You decide the mix.
            </p>
          </div>
        </Reveal>

        {/* Channel cards */}
        <div className="grid md:grid-cols-3 gap-[22px] items-start">
          {CHANNELS.map((ch, index) => (
            <Reveal variant="card" delay={index * 0.08} key={ch.name}>
              <article
                className={`glass-card relative flex flex-col p-7 lg:p-8 ${ch.featured ? "glass-featured" : ""}`}
              >
              {ch.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-[0.12em] text-white bg-brand-gradient px-4 py-1.5 rounded-full whitespace-nowrap" style={{ boxShadow: "0 6px 16px rgba(124,58,237,0.35)" }}>
                  {ch.badge}
                </div>
              )}
              <h3 className="text-[22px] font-[650] tracking-[-0.02em] mb-1.5">{ch.name}</h3>
              <p className="text-[13.5px] leading-[1.5] text-[#6c6c74] mb-4.5">{ch.description}</p>

              {/* Features */}
              <div className="flex flex-col gap-2.5 mb-5">
                {ch.features.map((f) => (
                  <div key={f} className="flex items-start gap-2.5">
                    <span className="check-circle mt-[1px]">✓</span>
                    <span className="text-[13px] text-[#55555c] leading-[1.45]">{f}</span>
                  </div>
                ))}
              </div>

              {/* Price */}
              <div className="flex items-baseline gap-2 mb-5">
                <span className="text-[34px] font-bold tracking-[-0.025em] whitespace-nowrap">{ch.price}</span>
                <span className="text-[13px] text-[#8e8e96] whitespace-nowrap">{ch.per}</span>
              </div>

              <Link
                to={ch.exploreTo}
                className="mt-auto inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#6d28d9] hover:opacity-80 transition-opacity whitespace-nowrap"
              >
                {ch.exploreLabel} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              </article>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}